import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

// Palette Corporate Premium (Dark Gray & Beige)
const colors = {
  bgDark: '#121214',
  bgMedium: '#1C1C1F',
  bgLight: '#252529',
  beigeLight: '#F5EFEB',
  beigeMain: '#D4C5B0',
  beigeDark: '#A3947F',
  textGray: '#A0A0A5',
  border: 'rgba(212, 197, 176, 0.15)',
};

const services = [
  {
    title: 'Litiges contractuels',
    description: 'Résolution des conflits liés à l\'exécution, l\'interprétation ou la rupture des contrats.',
    icon: '📝',
    features: ['Contrats commerciaux', 'Contrats de vente', 'Prestations de services', 'Baux commerciaux']
  },
  {
    title: 'Responsabilité civile',
    description: 'Défense rigoureuse en matière de responsabilité contractuelle et délictuelle.',
    icon: '⚖️',
    features: ['Dommages corporels', 'Dommages matériels', 'Préjudice moral', 'Faute professionnelle']
  },
  {
    title: 'Recouvrement de créances',
    description: 'Actions en recouvrement amiable et judiciaire pour sécuriser votre trésorerie.',
    icon: '💰',
    features: ['Injonction de payer', 'Saisies conservatoires', 'Exécution forcée', 'Procédures simplifiées']
  },
  {
    title: 'Conflits entre associés',
    description: 'Gestion stratégique des litiges internes au sein des structures sociétaires.',
    icon: '🤝',
    features: ['Exclusion d\'associé', 'Responsabilité des dirigeants', 'Conflits de gestion', 'Dissolution']
  },
  {
    title: 'Rupture commerciale',
    description: 'Litiges liés à la rupture brutale de relations commerciales établies.',
    icon: '📉',
    features: ['Rupture abusive', 'Préavis insuffisant', 'Manquements', 'Indemnisation']
  },
  {
    title: 'Modes alternatifs',
    description: 'Médiation, conciliation et arbitrage pour une résolution rapide et confidentielle.',
    icon: '🕊️',
    features: ['Médiation conventionnelle', 'Conciliation judiciaire', 'Arbitrage international', 'Transaction']
  }
];

const ContentieuxCivil = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [showStats, setShowStats] = useState(false);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  const galleryImages = [
    { src: '/images/P1.png', title: 'Procédure civile', description: 'Représentation devant les tribunaux' },
    { src: '/images/P2.jpg', title: 'Négociation', description: 'Résolution amiable des conflits' },
    { src: '/images/P3.jpg', title: 'Arbitrage', description: 'Procédure alternative et confidentielle' },
    { src: '/images/P4.png', title: 'Médiation', description: 'Recherche de consensus stratégique' }
  ];

  const processSteps = [
    { number: 'I', title: 'Analyse', description: 'Étude approfondie du dossier et des risques' },
    { number: 'II', title: 'Stratégie', description: 'Définition de la ligne de défense optimale' },
    { number: 'III', title: 'Action', description: 'Mise en œuvre des procédures judiciaires' },
    { number: 'IV', title: 'Résolution', description: 'Obtention du résultat et exécution' }
  ];

  // Calcul des styles responsives
  const computedStyles = useMemo(() => {
    return {
      section: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.bgDark} 0%, #17171A 50%, ${colors.bgDark} 100%)`,
        color: colors.beigeLight,
        minHeight: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      
      animatedBackground: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 1, overflow: 'hidden',
      },
      
      noiseOverlay: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E")`,
        pointerEvents: 'none', zIndex: 1,
      },
      
      gradientSphere: {
        position: 'absolute', top: '10%', right: '-10%',
        width: '50vw', height: '50vw',
        background: `radial-gradient(circle, rgba(212, 197, 176, 0.03) 0%, transparent 60%)`,
        filter: 'blur(80px)',
      },
      
      // Hero Section
      hero: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '6rem 1.5rem' : isTablet ? '8rem 3rem' : '10rem 5%',
      },
      
      heroContent: {
        maxWidth: '1440px', margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
        gap: isMobile ? '3rem' : '5rem',
        alignItems: 'center',
      },
      
      titleLine: {
        height: '1px',
        background: `linear-gradient(90deg, ${colors.beigeMain}, transparent)`,
        marginBottom: isMobile ? '1.5rem' : '2rem',
        width: isMobile ? '60px' : '100px',
      },
      
      mainTitle: {
        fontSize: isMobile ? '2.5rem' : isTablet ? '3.2rem' : '4.5rem',
        lineHeight: 1.1,
        fontWeight: 300,
        marginBottom: isMobile ? '1rem' : '1.5rem',
        color: colors.beigeLight,
        letterSpacing: '-1px',
      },
      
      titleHighlight: {
        color: colors.beigeMain,
        fontStyle: 'italic',
      },
      
      subtitle: {
        fontSize: isMobile ? '1.1rem' : isTablet ? '1.3rem' : '1.6rem',
        color: colors.textGray,
        fontWeight: 300,
        display: 'block',
        marginTop: isMobile ? '0.5rem' : '1rem',
      },
      
      heroTextWrapper: {
        display: 'flex', flexDirection: 'column', gap: '1.5rem',
        marginBottom: '3rem', marginTop: '2rem',
      },
      
      heroText: {
        fontSize: '1.05rem', lineHeight: 1.7, color: colors.textGray, margin: 0, fontWeight: 300,
        paddingLeft: '1.5rem', borderLeft: `1px solid ${colors.border}`,
      },
      
      statsContainer: {
        display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center',
        padding: '2rem 0', borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`,
      },
      
      statCard: {
        textAlign: isMobile ? 'center' : 'left', flex: 1, padding: isMobile ? '1rem 0' : '0 2rem',
      },
      
      statNumber: {
        fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 300, color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif", marginBottom: '0.2rem',
      },
      
      statLabel: {
        fontSize: '0.8rem', color: colors.textGray, textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      statDivider: {
        width: isMobile ? '50%' : '1px', height: isMobile ? '1px' : '50px', background: colors.border,
      },
      
      heroImageContainer: {
        position: 'relative', padding: '20px',
      },
      
      imageBackground: {
        position: 'absolute', top: '20px', left: '20px', right: '-20px', bottom: '-20px',
        border: `1px solid ${colors.beigeDark}`, borderRadius: '2px', zIndex: -1,
      },
      
      imageWrapper: {
        position: 'relative', height: isMobile ? '400px' : isTablet ? '500px' : '650px',
        borderRadius: '2px', overflow: 'hidden',
      },
      
      image: {
        width: '100%', height: '100%', objectFit: 'cover',
        filter: 'grayscale(25%) contrast(1.1) brightness(0.85)',
      },
      
      imageOverlay: {
        position: 'absolute', inset: 0,
        background: `linear-gradient(to top, rgba(18,18,20,0.9) 0%, transparent 50%)`,
      },
      
      imageBadge: {
        position: 'absolute', bottom: '0', left: '0',
        background: colors.bgMedium, padding: '1.5rem 2rem',
        borderTop: `1px solid ${colors.border}`, borderRight: `1px solid ${colors.border}`,
        display: 'flex', alignItems: 'center', gap: '1.2rem', zIndex: 2,
      },
      
      badgeIcon: {
        fontSize: '1.8rem',
        filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)', // Effet Bronze/Or
      },
      
      badgeText: {
        display: 'flex', flexDirection: 'column', gap: '0.2rem',
      },
      
      badgeTitle: {
        color: colors.beigeMain, fontSize: '1rem', fontWeight: 400, fontFamily: "'Playfair Display', serif",
      },
      
      badgeSubtitle: {
        color: colors.textGray, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      // Gallery Section (Notre Approche)
      gallerySection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight, borderTop: `1px solid ${colors.border}`,
      },
      
      sectionHeader: {
        textAlign: 'center', marginBottom: isMobile ? '3rem' : '5rem',
      },
      
      sectionTitle: {
        fontSize: isMobile ? '2rem' : '3rem', fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1rem', letterSpacing: '-0.5px',
      },
      
      sectionSubtitle: {
        fontSize: '1.1rem', color: colors.textGray, maxWidth: '600px', margin: '0 auto', fontWeight: 300,
      },
      
      galleryGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '1.5rem', maxWidth: '1440px', margin: '0 auto',
      },
      
      galleryItem: {
        position: 'relative', borderRadius: '2px', overflow: 'hidden', height: '400px',
        border: `1px solid ${colors.border}`,
      },
      
      itemOverlay: {
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: `linear-gradient(to top, ${colors.bgMedium} 0%, transparent 100%)`,
        padding: '2rem 1.5rem', color: 'white',
      },
      
      itemTitle: {
        fontSize: '1.2rem', color: colors.beigeMain, marginBottom: '0.5rem', fontWeight: 400, fontFamily: "'Playfair Display', serif",
      },
      
      itemDescription: {
        fontSize: '0.9rem', color: colors.textGray, fontWeight: 300, margin: 0,
      },
      
      // Services Section
      servicesSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
      },
      
      servicesGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '2rem', maxWidth: '1200px', margin: '0 auto',
      },
      
      serviceCard: {
        background: colors.bgMedium, borderRadius: '2px', padding: isMobile ? '2rem' : '3rem',
        border: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column',
      },
      
      serviceHeader: {
        display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${colors.border}`,
      },
      
      serviceIcon: {
        fontSize: '2rem', filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)',
      },
      
      serviceTitle: {
        fontSize: '1.3rem', fontWeight: 400, color: colors.beigeLight, margin: 0,
      },
      
      serviceDescription: {
        fontSize: '0.95rem', color: colors.textGray, lineHeight: 1.6, marginBottom: '2rem', flex: 1, fontWeight: 300,
      },
      
      serviceFeatures: {
        listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.8rem',
      },
      
      serviceFeature: {
        fontSize: '0.9rem', color: colors.beigeLight, display: 'flex', alignItems: 'center', gap: '0.8rem', fontWeight: 300,
      },
      
      featureDot: {
        width: '12px', height: '1px', backgroundColor: colors.beigeDark, flexShrink: 0,
      },
      
      // Process Section
      processSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight, borderTop: `1px solid ${colors.border}`,
      },
      
      processSteps: {
        display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between',
        position: 'relative', gap: isMobile ? '3rem' : 0, maxWidth: '1200px', margin: '4rem auto 0',
      },
      
      processConnector: {
        position: 'absolute', top: '30px', left: '10%', right: '10%', height: '1px',
        background: colors.border, zIndex: 1, display: isMobile ? 'none' : 'block',
      },
      
      processStep: {
        position: 'relative', zIndex: 2, textAlign: 'center', flex: 1,
      },
      
      stepCircle: {
        width: '60px', height: '60px', background: colors.bgDark, border: `1px solid ${colors.beigeMain}`,
        margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.2rem', fontWeight: '400', color: colors.beigeMain, fontFamily: "'Playfair Display', serif",
      },
      
      stepTitle: {
        fontSize: '1.1rem', fontWeight: 400, color: colors.beigeLight, marginBottom: '0.5rem',
      },
      
      stepDescription: {
        fontSize: '0.9rem', color: colors.textGray, fontWeight: 300, maxWidth: '200px', margin: '0 auto',
      },
    };
  }, [isMobile, isTablet]);

  // Animations Premium
  const premiumTransition = { duration: 1, ease: [0.16, 1, 0.3, 1] };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const fadeUpVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: premiumTransition }
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Contentieux Civil et Commercial | KMJ & Associés</title>
        <meta name="description" content="Expertise en contentieux civil et commercial : litiges contractuels, responsabilité civile, recouvrement de créances, conflits entre associés." />
      </Helmet>

      <motion.section style={computedStyles.section} initial="hidden" animate="visible" variants={containerVariants}>
        
        {/* Background Overlay */}
        <div style={computedStyles.animatedBackground}>
          <div style={computedStyles.gradientSphere} />
          <div style={computedStyles.noiseOverlay} />
        </div>

        {/* Hero Section */}
        <motion.div style={computedStyles.hero} variants={containerVariants}>
          <div style={computedStyles.heroContent}>
            <motion.div variants={fadeUpVariants}>
              <div style={computedStyles.titleLine} />
              <h1 style={computedStyles.mainTitle}>
                Contentieux civil
                <br />
                <span style={computedStyles.titleHighlight}>et commercial.</span>
              </h1>
              <span style={computedStyles.subtitle}>
                Expertise et stratégie judiciaire.
              </span>

              <motion.div style={computedStyles.heroTextWrapper}>
                <p style={computedStyles.heroText}>
                  Le cabinet KMJ & Associés intervient avec rigueur en matière de contentieux civil et commercial, représentant vos intérêts devant toutes les juridictions compétentes.
                </p>
                <p style={computedStyles.heroText}>
                  Nous assurons une gestion stratégique de vos litiges contractuels, responsabilités civiles ou conflits d'associés, en privilégiant systématiquement la résolution la plus protectrice pour votre structure.
                </p>
              </motion.div>

              {/* Stats Bar */}
              <motion.div style={computedStyles.statsContainer}>
                <div style={computedStyles.statCard}>
                  <motion.div style={computedStyles.statNumber} initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1 }}>
                    95%
                  </motion.div>
                  <div style={computedStyles.statLabel}>Taux de succès</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statCard}>
                  <motion.div style={computedStyles.statNumber} initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1, delay: 0.2 }}>
                    1000+
                  </motion.div>
                  <div style={computedStyles.statLabel}>Dossiers traités</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statCard}>
                  <motion.div style={computedStyles.statNumber} initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1, delay: 0.4 }}>
                    24h
                  </motion.div>
                  <div style={computedStyles.statLabel}>Réactivité</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image & Offset Frame */}
            <motion.div style={computedStyles.heroImageContainer} variants={fadeUpVariants}>
              <div style={computedStyles.imageBackground} />
              <div style={computedStyles.imageWrapper}>
                <img src="/images/L10.jpg" alt="Contentieux civil et commercial" style={computedStyles.image} loading="lazy" />
                <div style={computedStyles.imageOverlay} />
                <div style={computedStyles.imageBadge}>
                  <div style={computedStyles.badgeIcon}>⚖️</div>
                  <div style={computedStyles.badgeText}>
                    <div style={computedStyles.badgeTitle}>Département Contentieux</div>
                    <div style={computedStyles.badgeSubtitle}>Plaidoyer & Négociation</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Gallery Section */}
        <div style={computedStyles.gallerySection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Notre <span style={computedStyles.titleHighlight}>Approche</span>
            </motion.h2>
            <motion.p style={computedStyles.sectionSubtitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Une méthodologie rigoureuse adaptée à la complexité de chaque litige.
            </motion.p>
          </div>

          <div style={computedStyles.galleryGrid}>
            {galleryImages.map((image, index) => (
              <motion.div
                key={index} style={computedStyles.galleryItem}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark }}
              >
                <img src={image.src} alt={image.title} style={computedStyles.image} />
                <div style={computedStyles.itemOverlay}>
                  <h3 style={computedStyles.itemTitle}>{image.title}</h3>
                  <p style={computedStyles.itemDescription}>{image.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services Section */}
        <div style={computedStyles.servicesSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Domaines <span style={computedStyles.titleHighlight}>d'intervention</span>
            </motion.h2>
          </div>

          <div style={computedStyles.servicesGrid}>
            {services.map((service, index) => (
              <motion.div
                key={index} style={computedStyles.serviceCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
              >
                <div style={computedStyles.serviceHeader}>
                  <div style={computedStyles.serviceIcon}>{service.icon}</div>
                  <h3 style={computedStyles.serviceTitle}>{service.title}</h3>
                </div>
                <p style={computedStyles.serviceDescription}>{service.description}</p>
                <ul style={computedStyles.serviceFeatures}>
                  {service.features.map((feature, i) => (
                    <li key={i} style={computedStyles.serviceFeature}>
                      <div style={computedStyles.featureDot} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Section */}
        <div style={computedStyles.processSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Notre <span style={computedStyles.titleHighlight}>Processus</span>
            </motion.h2>
          </div>

          <div style={computedStyles.processSteps}>
            <div style={computedStyles.processConnector} />
            {processSteps.map((step, index) => (
              <motion.div
                key={index} style={computedStyles.processStep}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.2 } } }}
              >
                <div style={computedStyles.stepCircle}>{step.number}</div>
                <h3 style={computedStyles.stepTitle}>{step.title}</h3>
                <p style={computedStyles.stepDescription}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

      </motion.section>
    </>
  );
};

export default ContentieuxCivil;